import React from "react";
import { Award, BookOpen, Heart, ScrollText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { certifications, awards, publications, interests } from "../data/mock";

const ExtraBits = () => {
  return (
    <section id="extras" className="relative py-24 lg:py-32 border-t border-teal-500/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-teal-400 mb-4">
            <span className="w-8 h-px bg-teal-400" /> 07 — Extra Bits
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold leading-tight">
            The <span className="text-teal-300">other things</span> that shape my work.
          </h2>
          <p className="mt-4 text-slate-400">
            Certifications, awards, publications, and the personal interests that keep me curious outside of class.
          </p>
        </div>

        <Tabs defaultValue="certs" className="w-full">
          <TabsList className="bg-[#08201d]/80 border border-teal-500/15 p-1 h-auto flex flex-wrap gap-1">
            <TabsTrigger
              value="certs"
              className="data-[state=active]:bg-teal-400 data-[state=active]:text-[#04100f] text-slate-300 px-4 py-2"
            >
              <ScrollText className="w-4 h-4 mr-2" />
              Certifications
            </TabsTrigger>
            <TabsTrigger
              value="awards"
              className="data-[state=active]:bg-teal-400 data-[state=active]:text-[#04100f] text-slate-300 px-4 py-2"
            >
              <Award className="w-4 h-4 mr-2" />
              Awards
            </TabsTrigger>
            <TabsTrigger
              value="pubs"
              className="data-[state=active]:bg-teal-400 data-[state=active]:text-[#04100f] text-slate-300 px-4 py-2"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Publications
            </TabsTrigger>
            <TabsTrigger
              value="interests"
              className="data-[state=active]:bg-teal-400 data-[state=active]:text-[#04100f] text-slate-300 px-4 py-2"
            >
              <Heart className="w-4 h-4 mr-2" />
              Interests
            </TabsTrigger>
          </TabsList>

          <TabsContent value="certs" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {certifications.map((c) => (
                <div
                  key={c.name}
                  className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-5 flex items-start gap-4 hover:border-teal-400/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
                    <ScrollText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-100">{c.name}</div>
                    <div className="text-sm text-slate-400 mt-0.5">
                      {c.issuer} · <span className="text-teal-300">{c.year}</span>
                    </div>
                    {c.credentialId && (
                      <div className="text-xs text-slate-500 mt-1 font-mono">
                        ID: {c.credentialId}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="awards" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {awards.map((a) => (
                <div
                  key={a.name}
                  className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-5 flex items-start gap-4 hover:border-teal-400/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-100">{a.name}</div>
                    <div className="text-sm text-teal-300 mt-0.5 font-mono">{a.year}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pubs" className="mt-6">
            <div className="space-y-4">
              {publications.map((p) => (
                <div
                  key={p.title}
                  className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-6 hover:border-teal-400/40 transition-colors"
                >
                  <div className="flex items-center gap-2 text-xs font-mono text-teal-400 uppercase tracking-wider">
                    <BookOpen className="w-4 h-4" />
                    {p.year}
                  </div>
                  <div className="font-display text-lg font-semibold text-slate-100 mt-2">
                    {p.title}
                  </div>
                  <div className="text-sm text-slate-400 mt-1">{p.venue}</div>
                </div>
              ))}
              <p className="text-sm text-slate-500 italic">
                More writing in progress — check back soon.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="interests" className="mt-6">
            <div className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-7">
              <p className="text-slate-300 mb-5">
                When I&apos;m not in the lab or behind a CAD screen, you&apos;ll probably find me doing one of these:
              </p>
              <div className="flex flex-wrap gap-2">
                {interests.map((i) => (
                  <Badge
                    key={i}
                    className="bg-teal-500/10 hover:bg-teal-500/20 text-teal-200 border border-teal-500/30 text-sm py-1.5 px-3 font-normal"
                  >
                    {i}
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ExtraBits;
